import { Trash2, Volume2 } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { stickerUrl, sfxLabel, stickerLabel } from "@/components/editor/stickers/fx";

const sel = "bg-[#070912] border border-white/10 rounded px-1 py-0.5 text-[10px] text-slate-300 focus:outline-none";

const TimeInput = ({ value, onChange, testid }) => (
  <input type="number" min="0" step="0.1" value={value} onChange={(e) => onChange(Math.max(0, Number(e.target.value) || 0))}
    className={`${sel} w-14 font-mono`} data-testid={testid} />
);

const Range = ({ label, value, min, max, step, onChange, testid, fmt }) => (
  <label className="flex items-center gap-2 text-[10px] text-slate-400">
    <span className="w-14 shrink-0">{label}</span>
    <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} className="flex-1 accent-[#3ad7ff]" data-testid={testid} />
    <span className="font-mono w-10 text-right text-slate-300">{fmt(value)}</span>
  </label>
);

export const PlacedList = ({ cfg, cat, update, remove, only }) => {
  const { t, lang } = useI18n();
  const stickers = only === "sfx" ? [] : cfg.stickers || [];
  const sfx = only === "stickers" ? [] : cfg.sfx || [];
  if (!stickers.length && !sfx.length) return null;
  return (
    <div className="space-y-1.5" data-testid="placed-items">
      {stickers.map((s, i) => (
        <div key={`s${i}`} className="text-xs rounded-md bg-white/[0.03] px-2 py-1.5 space-y-1.5" data-testid="placed-sticker">
          <div className="flex items-center gap-2">
            <img src={stickerUrl(s)} alt="" className="h-7 w-12 object-contain shrink-0" />
            {s.id === "custom" ? (
              <input value={s.text || ""} maxLength={24} onChange={(e) => update("stickers", i, { text: e.target.value.toUpperCase() })}
                className={`${sel} flex-1 min-w-0 uppercase`} data-testid={`placed-sticker-text-${i}`} />
            ) : <span className="flex-1 min-w-0 text-white text-[11px] font-semibold uppercase truncate">{stickerLabel(cat, s)}</span>}
            <button onClick={() => remove("stickers", i)} className="text-slate-500 hover:text-rose-400 shrink-0" data-testid="remove-sticker-btn"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
          <div className="grid grid-cols-[auto_1fr_auto_1fr] sm:grid-cols-[auto_auto_auto_auto_auto] gap-1.5 items-center">
          <TimeInput value={s.t} onChange={(v) => update("stickers", i, { t: v })} testid={`placed-sticker-time-${i}`} />
          <select value={s.anim || "pop"} onChange={(e) => update("stickers", i, { anim: e.target.value })} className={sel} data-testid={`placed-sticker-anim-${i}`}>
            {cat.anims.map((a) => <option key={a} value={a}>{t(`st.anim_${a}`)}</option>)}
          </select>
          <select value={s.d || 1.6} onChange={(e) => update("stickers", i, { d: Number(e.target.value) })} className={sel} data-testid={`placed-sticker-d-${i}`}>
            {[0.8, 1.2, 1.6, 2, 2.5, 3, 4].map((a) => <option key={a} value={a}>{a}s</option>)}
          </select>
          {s.id === "custom" && (
            <select value={s.style || "chrome"} onChange={(e) => update("stickers", i, { style: e.target.value })} className={sel} data-testid={`placed-sticker-style-${i}`}>
              {cat.styles.map((a) => <option key={a} value={a}>{t(`st.style_${a}`)}</option>)}
            </select>
          )}
          </div>
          <Range label={t("st.size")} value={s.scale ?? 1} min={0.2} max={3} step={0.05} onChange={(v) => update("stickers", i, { scale: v })} testid={`placed-sticker-scale-${i}`} fmt={(v) => `${Math.round(v * 100)}%`} />
          <Range label={t("st.rotation")} value={s.rot ?? 0} min={-180} max={180} step={1} onChange={(v) => update("stickers", i, { rot: v })} testid={`placed-sticker-rot-${i}`} fmt={(v) => `${v}°`} />
        </div>
      ))}
      {sfx.map((s, i) => (
        <div key={`a${i}`} className="grid grid-cols-[auto_minmax(0,1fr)_auto_64px_32px_auto] items-center gap-2 text-xs rounded-md bg-white/[0.03] px-2 py-1.5" data-testid="placed-sfx">
          <Volume2 className="w-3.5 h-3.5 text-[#3ad7ff]" /><span className="text-slate-300 truncate">{sfxLabel(cat, s.id, lang)}</span>
          <TimeInput value={s.t} onChange={(v) => update("sfx", i, { t: v })} testid={`placed-sfx-time-${i}`} />
          <input type="range" min="0" max="2" step="0.1" value={s.vol ?? 1} onChange={(e) => update("sfx", i, { vol: Number(e.target.value) })}
            className="w-16 accent-[#3ad7ff]" data-testid={`placed-sfx-vol-${i}`} />
          <span className="font-mono text-[10px] text-slate-500 w-8">{Math.round((s.vol ?? 1) * 100)}%</span>
          <button onClick={() => remove("sfx", i)} className="text-slate-500 hover:text-rose-400" data-testid="remove-sfx-btn"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
    </div>
  );
};
